
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Modal } from "react-bootstrap";
import moment from "moment";
import { FiCheckCircle, FiLock, FiLogOut, FiRefreshCw } from "react-icons/fi";


import { ButtonView, Input } from "../../../controls";
import BookRoomTab from "./BookRoomTab";

const PASTEL_COLORS = [
  "#fee2e2", // red-100
  "#ffedd5", // orange-100
  "#fef9c3", // yellow-100
  "#dcfce7", // green-100
  "#ccfbf1", // teal-100
  "#e0f2fe", // sky-100
  "#e0e7ff", // indigo-100
  "#f3e8ff", // purple-100
  "#fae8ff", // fuchsia-100
  "#ffe4e6", // rose-100
];

const BORDER_COLORS = [
  "#ef4444", // red-500
  "#f97316", // orange-500
  "#eab308", // yellow-500
  "#22c55e", // green-500
  "#14b8a6", // teal-500
  "#0ea5e9", // sky-500
  "#6366f1", // indigo-500
  "#a855f7", // purple-500
  "#d946ef", // fuchsia-500
  "#f43f5e", // rose-500
];

const getBookingStyles = (bookingId) => {
  if (!bookingId) return { backgroundColor: "#f1f5f9", borderLeft: "4px solid #64748b", color: "#1e293b" };
  let hash = 0;
  for (let i = 0; i < bookingId.length; i++) {
    hash = bookingId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % PASTEL_COLORS.length;
  return {
    backgroundColor: PASTEL_COLORS[index],
    borderLeft: `4px solid ${BORDER_COLORS[index]}`,
    color: "#1e293b",
  };
};

const StatusTimelineTab = ({
  roomList,
  userList,
  user,
  currentOccupancyList,
  getCurrentOccupancy,
  occupancyHistoryList,
  getOccupancyHistory,
  checkOutRoom,
  checkoutPending,
  cancelBooking,
  occupancyHistoryPending,
  locationList,
  bookRoom,
  bookPending,
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOccupancy, setSelectedOccupancy] = useState(null);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [occupancyToCheckout, setOccupancyToCheckout] = useState(null);
  const bookSubmitRef = useRef(null);

  useEffect(() => {
    getCurrentOccupancy();
  }, [getCurrentOccupancy]);

  useEffect(() => {
    const fromDate = moment(selectedDate).startOf("day").toISOString();
    const toDate = moment(selectedDate).endOf("day").toISOString();
    getOccupancyHistory({ fromDate, toDate, limit: 200 });
  }, [getOccupancyHistory, selectedDate]);

  const handleRefresh = () => {
    getCurrentOccupancy();
    const fromDate = moment(selectedDate).startOf("day").toISOString();
    const toDate = moment(selectedDate).endOf("day").toISOString();
    getOccupancyHistory({ fromDate, toDate, limit: 200 });
  };

  const handleEarlyCheckOut = (occupancyId) => {
    setOccupancyToCheckout(occupancyId);
    setShowCheckoutModal(true);
  };

  const handleConfirmCheckout = () => {
    if (occupancyToCheckout) {
      checkOutRoom({
        occupancyId: occupancyToCheckout,
        checkOutTime: new Date().toISOString(),
      });
      setShowCheckoutModal(false);
      setOccupancyToCheckout(null);
      setTimeout(handleRefresh, 800);
    } else {
      console.error("handleConfirmCheckout failed: occupancyToCheckout is falsy.", occupancyToCheckout);
    }
  };

  const handleCancelClick = (occupancy) => {
    setSelectedOccupancy(occupancy);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    if (selectedOccupancy) {
      cancelBooking({
        occupancyId: selectedOccupancy._id,
      });
      setShowCancelModal(false);
      setSelectedOccupancy(null);
      setTimeout(handleRefresh, 800);
    }
  };

  // Timeline range: 08:00 to 21:00 (8 AM to 9 PM) = 13 hours total
  const TIMELINE_START_HOUR = 8; // 8:00 AM
  const TIMELINE_END_HOUR = 21;  // 9:00 PM

  // Generate hour labels for header (and 15-min ticks)
  const timeSlotLabels = [];
  for (let h = TIMELINE_START_HOUR; h <= TIMELINE_END_HOUR; h++) {
    const label = h < 12 ? `${h}:00 AM` : h === 12 ? "12:00 PM" : `${h - 12}:00 PM`;
    timeSlotLabels.push({ hour: h, label, isHour: true });
    if (h !== TIMELINE_END_HOUR) {
      timeSlotLabels.push({ hour: h + 0.25, label: "", isHour: false });
      timeSlotLabels.push({ hour: h + 0.5, label: "", isHour: false });
      timeSlotLabels.push({ hour: h + 0.75, label: "", isHour: false });
    }
  }

  // Convert a Date/string to fractional hours from midnight (e.g. 08:30 => 8.5)
  const toFractionalHour = (dt) => moment(dt).hour() + moment(dt).minute() / 60;

  // Clamp a value between min and max
  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

  // Filter history logs for the selected timeline date
  const selectedDateStr = moment(selectedDate).format("YYYY-MM-DD");
  const dayBookings = (occupancyHistoryList || []).filter((booking) => {
    return moment(booking.checkInTime).format("YYYY-MM-DD") === selectedDateStr && !booking.isCancelled;
  });

  // Calculate dynamic width for each hour slot [H, H+1]
  const hourWidths = {};
  const BASE_HOUR_WIDTH = 250; // Base width for an hour in px (compact by default)
  const EXTRA_WIDTH_PER_BOOKING = 180; // Expand by 180px per extra booking in that slot to prevent congestion

  for (let h = TIMELINE_START_HOUR; h < TIMELINE_END_HOUR; h++) {
    let maxBookings = 0;
    roomList.forEach(room => {
      const bookingsInRoom = dayBookings.filter(b => {
        if (b.roomId && b.roomId._id === room._id) {
          const startH = toFractionalHour(b.checkInTime);
          const endH = b.checkOutTime ? toFractionalHour(b.checkOutTime) : TIMELINE_END_HOUR;
          return startH < h + 1 && endH > h;
        }
        return false;
      });
      if (bookingsInRoom.length > maxBookings) {
        maxBookings = bookingsInRoom.length;
      }
    });
    // Only apply expansion logic if there are multiple bookings (maxBookings > 1)
    const extraWidth = maxBookings > 1 ? (maxBookings - 1) * EXTRA_WIDTH_PER_BOOKING : 0;
    hourWidths[h] = BASE_HOUR_WIDTH + extraWidth;
  }

  // Compute cumulative left positions in pixels
  const cumLeftPx = {};
  let totalWidthPx = 30; // Add 30px offset so the first time label doesn't overlap the sticky room name column
  for (let h = TIMELINE_START_HOUR; h <= TIMELINE_END_HOUR; h++) {
    cumLeftPx[h] = totalWidthPx;
    if (h < TIMELINE_END_HOUR) {
      totalWidthPx += hourWidths[h];
    }
  }
  totalWidthPx += 30; // Add 30px offset to the end so the last time label isn't cut off

  // Helper to translate fractional hour to pixel left position
  const getLeftPx = (fractionalHour) => {
    const clampedHour = clamp(fractionalHour, TIMELINE_START_HOUR, TIMELINE_END_HOUR);
    const H = Math.floor(clampedHour);
    const f = clampedHour - H;
    const startPx = cumLeftPx[H] !== undefined ? cumLeftPx[H] : 0;
    // For the very end hour boundary (21:00), just return the accumulated width
    if (H === TIMELINE_END_HOUR) {
      return cumLeftPx[TIMELINE_END_HOUR];
    }
    const slotWidth = hourWidths[H] || BASE_HOUR_WIDTH;
    return startPx + f * slotWidth;
  };

  // Render cards for Current Occupancy
  const renderOccupancyCards = () => {
    return (
      <Row className="mb-4">
        {roomList.map((room) => {
          // Find current occupancy record for this room
          const currentOcc = (currentOccupancyList || []).find(
            (occ) => occ.roomId && occ.roomId._id === room._id
          );

          const isOccupied = currentOcc && (currentOcc.status === "Occupied" || currentOcc.status === "Occupied (Private)");
          const isShared = currentOcc && (currentOcc.status === "Shared" || currentOcc.status === "Occupied (Shared)");
          const inUse = isOccupied || isShared;

          const isCurrentUserOccupying =
            inUse &&
            currentOcc.employeeId &&
            user &&
            currentOcc.employeeId._id === user._id;

          const occupyingEmployee =
            currentOcc && currentOcc.employeeId
              ? `${currentOcc.employeeId.firstName} ${currentOcc.employeeId.lastName}`
              : null;

          let badgeText = "Available Now";
          let badgeVariant = "success-badge";
          let icon = <FiCheckCircle className="status-icon success-color" size={24} />;

          if (isOccupied) {
            badgeText = "Private";
            badgeVariant = "danger-badge";
            icon = <FiLock className="status-icon danger-color" size={24} />;
          } else if (isShared) {
            badgeText = "Shared";
            badgeVariant = "warning-badge";
            icon = <FiLock className="status-icon warning-color" size={24} />;
          }

          return (
            <Col md={4} key={room._id} className="mb-3">
              <Card className="occupancy-card h-100">
                <Card.Body className="d-flex flex-column justify-content-between p-3">
                  <div>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h5 className="room-title mb-1">{room.name}</h5>
                        <div className="room-meta">
                          {room.location && typeof room.location === "object" ? (room.location.name || "") : (room.location || "")}
                        </div>
                      </div>
                      {icon}
                    </div>
                    <div className="my-2">
                      <span className={`status-dot-badge ${badgeVariant}`}>
                        <span className="dot"></span>
                        {badgeText}
                      </span>
                    </div>
                    {occupyingEmployee && (
                      <div className="details-text mt-1" style={{ fontSize: "12px", color: "#64748b" }}>
                        <div><strong>Occupied by:</strong> {occupyingEmployee}</div>
                        {currentOcc.checkInTime && (
                          <div className="mt-1">
                            <strong>Time:</strong> {moment(currentOcc.checkInTime).format("hh:mm A")} - {currentOcc.checkOutTime ? moment(currentOcc.checkOutTime).format("hh:mm A") : "09:00 PM"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {isCurrentUserOccupying && currentOcc && (
                    <div className="mt-3 border-top pt-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const occIdToUse = currentOcc.occupancyId || currentOcc._id || currentOcc.id;
                          if (!occIdToUse) {
                            console.error("Could not find occupancy ID in currentOcc", currentOcc);
                          }
                          handleEarlyCheckOut(occIdToUse);
                        }}
                        className="btn-link-action d-flex align-items-center"
                      >
                        <FiLogOut className="me-1" /> Check-out Early
                      </button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  // Render Timeline Grid using absolute positioning
  const renderTimelineGrid = () => {
    return (
      <div className="timeline-container mt-3">
        <div className="d-flex justify-content-end align-items-center mb-3">
          <div className="d-flex align-items-center">
            <Input
              type="date"
              name="timelineDate"
              value={moment(selectedDate).format("YYYY-MM-DD")}
              onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : new Date())}
            />
          </div>
        </div>

        <div className="timeline-scroll-wrapper">
          {/* Header: time labels */}
          <div className="timeline-header-row">
            <div className="timeline-room-label-header">Room</div>
            <div className="timeline-grid-header" style={{ width: `${totalWidthPx}px`, minWidth: `${totalWidthPx}px` }}>
              {timeSlotLabels.map((slot, idx) => (
                <div
                  key={idx}
                  className="timeline-hour-marker"
                  style={{
                    left: `${getLeftPx(slot.hour)}px`,
                  }}
                >
                  {slot.isHour && <span className="timeline-hour-label">{slot.label}</span>}
                  <div className="timeline-hour-tick" style={{ height: slot.isHour ? '6px' : '4px', backgroundColor: 'rgba(243, 95, 41, 0.4)' }} />
                </div>
              ))}
            </div>
          </div>

          {/* Rows: one per room */}
          {roomList.map((room) => {
            const roomBookings = dayBookings
              .filter((b) => b.roomId && b.roomId._id === room._id)
              .sort((a, b) => moment(a.checkInTime).diff(moment(b.checkInTime)));

            // Calculate lanes to prevent visual overlap in pixels
            const lanesPx = [];
            const processedBookings = roomBookings.map((booking) => {
              const startH = clamp(toFractionalHour(booking.checkInTime), TIMELINE_START_HOUR, TIMELINE_END_HOUR);
              const rawEnd = booking.checkOutTime ? toFractionalHour(booking.checkOutTime) : TIMELINE_END_HOUR;
              const endH = clamp(rawEnd, TIMELINE_START_HOUR, TIMELINE_END_HOUR);

              const leftPx = getLeftPx(startH);
              const rightPx = getLeftPx(endH);
              const widthPx = Math.max(rightPx - leftPx, 140); // minimum 140px visual card width to keep text visible

              // We stack if the cards visually overlap. We assume a minimum readability zone of 150px for stacking rules.
              const visualEndPx = leftPx + widthPx;

              let laneIndex = lanesPx.findIndex(laneEndPx => laneEndPx <= leftPx);
              if (laneIndex === -1) {
                lanesPx.push(visualEndPx);
                laneIndex = lanesPx.length - 1;
              } else {
                lanesPx[laneIndex] = visualEndPx;
              }

              return { booking, leftPx, widthPx, laneIndex };
            });

            // Base row height is 80px, each additional lane adds 60px
            const rowHeight = Math.max(80, (lanesPx.length * 60) + 20);

            return (
              <div key={room._id} className="timeline-room-row" style={{ minHeight: `${rowHeight}px` }}>
                <div className="timeline-room-label">
                  <strong>{room.name}</strong>
                </div>
                <div className="timeline-grid-area" style={{ width: `${totalWidthPx}px`, minWidth: `${totalWidthPx}px` }}>
                  {/* Background dividers */}
                  {timeSlotLabels.map((slot, idx) => (
                    <div
                      key={idx}
                      className="timeline-grid-line"
                      style={{
                        left: `${getLeftPx(slot.hour)}px`,
                        borderLeft: '1px solid rgba(243, 95, 41, 0.2)'
                      }}
                    />
                  ))}



                  {processedBookings.map(({ booking, leftPx, widthPx, laneIndex }) => {
                    const isOwner = user && booking.employeeId && user._id === booking.employeeId._id;
                    const employeeName = booking.employeeId
                      ? `${booking.employeeId.firstName} ${booking.employeeId.lastName}`
                      : "Staff";
                    const startFmt = moment(booking.checkInTime).format("h:mm a");
                    const endFmt = booking.checkOutTime
                      ? moment(booking.checkOutTime).format("h:mm a")
                      : "9:00 PM";

                    return (
                      <div
                        key={booking._id}
                        className="timeline-booking-block"
                        style={{
                          left: `${leftPx}px`,
                          width: `${widthPx}px`,
                          top: `${6 + (laneIndex * 60)}px`,
                          height: '56px',
                          ...getBookingStyles(booking._id),
                        }}
                        title={`${employeeName}: ${booking.purpose || "Meeting"} (${startFmt} – ${endFmt})`}
                      >
                        <div className="tl-booking-title">{booking.purpose || "Meeting"}</div>
                        <div className="tl-booking-employee">{employeeName}</div>
                        <div className="tl-booking-time">{startFmt} – {endFmt}</div>
                        {isOwner && moment(booking.checkInTime).isAfter(moment()) && (
                          <button
                            type="button"
                            className="tl-cancel-btn"
                            style={{ pointerEvents: "auto", position: "absolute", zIndex: 1060 }}
                            onClick={(e) => { e.stopPropagation(); handleCancelClick(booking); }}
                          >
                            &times; Cancel
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="status-timeline-tab mt-2">
      {/* Current Occupancy Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="section-title m-0">Current Occupancy</h4>
        <button
          type="button"
          className="btn p-0 d-flex align-items-center justify-content-center"
          onClick={getCurrentOccupancy}
          title="Refresh Occupancy"
          style={{ borderRadius: "50%", width: "32px", height: "32px", border: "none", color: "var(--secondary)", backgroundColor: "transparent" }}
        >
          <FiRefreshCw size={18} strokeWidth={2.8} />
        </button>
      </div>
      {renderOccupancyCards()}

      {/* Booking Timeline Section */}
      <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
        <h4 className="section-title m-0">Booking Timeline</h4>
        <ButtonView
          onClick={() => setShowBookModal(true)}
          title="Book Room"
          styles="secondary"
        />
      </div>
      <Card>
        <Card.Body className="p-3">
          {renderTimelineGrid()}
        </Card.Body>
      </Card>

      {/* Book Room Modal */}
      <Modal show={showBookModal} onHide={() => setShowBookModal(false)} size="lg" scrollable aria-labelledby="book-room-modal">
        <Modal.Header>
          <Modal.Title id="book-room-modal">Book a Room</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BookRoomTab
            roomList={roomList}
            userList={userList}
            user={user}
            bookRoom={bookRoom}
            bookPending={bookPending}
            isModal={true}
            onClose={() => {
              setShowBookModal(false);
              setTimeout(handleRefresh, 800);
            }}
            submitRef={bookSubmitRef}
          />
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowBookModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            style={{ backgroundColor: "var(--secondary)", color: "#fff", border: "none" }}
            onClick={() => bookSubmitRef.current && bookSubmitRef.current()}
            disabled={bookPending}
          >
            {bookPending ? "Booking..." : "Book Room"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} animation={false} centered>
        <Modal.Header>
          <Modal.Title>Cancel Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel the booking for{" "}
          <strong>{selectedOccupancy && selectedOccupancy.roomId ? selectedOccupancy.roomId.name : ""}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ pointerEvents: "auto", position: "relative", zIndex: 1060 }}
            onClick={(e) => { e.stopPropagation(); setShowCancelModal(false); }}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-danger"
            style={{ pointerEvents: "auto", position: "relative", zIndex: 1060 }}
            onClick={(e) => { e.stopPropagation(); handleConfirmCancel(); }}
          >
            Confirm Cancel
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCheckoutModal} onHide={() => setShowCheckoutModal(false)} animation={false} centered>
        <Modal.Header>
          <Modal.Title>Check-out Early</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to check out of this room early?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ pointerEvents: "auto", position: "relative", zIndex: 1060 }}
            onClick={(e) => { e.stopPropagation(); setShowCheckoutModal(false); }}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-danger"
            style={{ pointerEvents: "auto", position: "relative", zIndex: 1060 }}
            onClick={(e) => { e.stopPropagation(); handleConfirmCheckout(); }}
          >
            Confirm Check-out
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

StatusTimelineTab.propTypes = {
  roomList: PropTypes.array.isRequired,
  userList: PropTypes.array.isRequired,
  user: PropTypes.object,
  currentOccupancyList: PropTypes.array.isRequired,
  getCurrentOccupancy: PropTypes.func.isRequired,
  occupancyHistoryList: PropTypes.array.isRequired,
  getOccupancyHistory: PropTypes.func.isRequired,
  checkOutRoom: PropTypes.func.isRequired,
  checkoutPending: PropTypes.bool,
  cancelBooking: PropTypes.func.isRequired,
  occupancyHistoryPending: PropTypes.bool,
  locationList: PropTypes.array.isRequired,
  bookRoom: PropTypes.func.isRequired,
  bookPending: PropTypes.bool,
};

export default StatusTimelineTab;


